import type { GalleryItem } from "@/modules/companies/api/types"
import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useState } from "react"

import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"

import { MediaVideo } from "@/features/companyDetail/components/shared/Media/Video"
import { MEDIA_TYPE_VIDEO } from "@/modules/companies/constants/enums"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useTracking } from "@/modules/tracking/useTracking"

import styles from "./VideosGallery.module.css"

const VideosModalDynamic = dynamic(() =>
  import("@/features/companyDetail/components/Index/MediaGallery/VideosModal").then(mod => mod.VideosModal)
)

const MAX_VIDEOS_GALLERY_LENGTH = 3

export const VideosGallery: FunctionComponent = () => {
  const tracking = useTracking()
  const { t } = useTranslation(["companies_client"])
  const { dataCompany } = useCompanyFetch()
  const [showModal, setShowModal] = useState<boolean>(false)

  if (!dataCompany) return null

  const {
    id,
    page: { name, gallery },
  } = dataCompany

  const galleryVideo =
    (gallery.filter(media => media.type === MEDIA_TYPE_VIDEO) as GalleryItem<"MEDIA_TYPE_VIDEO">[]) || []

  if (galleryVideo.length < 1) return null

  const displayModal = (): void => {
    setShowModal(true)
    tracking?.track("see_more_information_click", {
      company_id: id,
      company_name: name,
      context: "videos_gallery",
      service: "company",
    })
  }

  return (
    <div className={styles.main} data-testid="company_information_deepDive_videosGallery">
      <div className={styles.videosGallery}>
        {galleryVideo.slice(0, MAX_VIDEOS_GALLERY_LENGTH).map(currentMedia => (
          <MediaVideo key={currentMedia.url} video={currentMedia} tracking={{ id, name }} />
        ))}
      </div>
      {galleryVideo.length > MAX_VIDEOS_GALLERY_LENGTH && (
        <Button minor variant={BUTTON_VARIANT.GHOST} className={styles.videosGallery__showModal} onClick={displayModal}>
          {t("companies_client.media.videosGallery.button", { count: galleryVideo.length })}
        </Button>
      )}
      {showModal && (
        <VideosModalDynamic gallery={galleryVideo} onClose={() => setShowModal(false)} videoTracking={{ id, name }} />
      )}
    </div>
  )
}
